import React, { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import loginSideImg from "../../assets/login-screen.png";
import enphaseLogoWithText from "../../assets/enphase_logo_with_text.png";
import useAuth from "./hooks/useAuth";
import { TEXT_LIBRARY } from "../../lib/utils/constants";
import SnackbarUtils from "../../lib/components/Snackbar/utils";
import { SIGNUP_URL, FORGOT_PASSWORD_URL, PRIVACY_URL, TERMS_URL } from "./constants";
import { getCopyrightText } from "../../lib/utils/utility";

const Login = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const { login, loading } = useAuth();

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      const formData = { email: email, password: password, locale: "en" };
      const data = await login(formData);
      let systemId = data?.system_id || data?.user?.system_id || "";
      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      const startDate1 = `${yyyy}-${mm}-${dd}`;
      const endDate1 = `${yyyy}-${mm}-${dd}`;
      const params = new URLSearchParams({
        systemId,
        startDate1,
        endDate1,
      }).toString();
      navigate(`/?${params}`, { replace: true });
    } catch (err) {
      let errorMessage = TEXT_LIBRARY.INVALID_CREDENTIALS;
      if (err.response?.data?.status === "UNAUTHORIZED") {
        errorMessage = TEXT_LIBRARY.INVALID_CREDENTIALS;
      } else {
        errorMessage = err.response?.data || err.message || TEXT_LIBRARY.INVALID_CREDENTIALS;
      }
      SnackbarUtils.error(`${errorMessage}`);
    }
  };

  const EnphaseLogo = ({ className = "" }) => (
    <img
      src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABsAAAAcCAYAAACQ0cTtAAAAAXNSR0IArs4c6QAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAG6ADAAQAAAABAAAAHAAAAABn56rTAAAC80lEQVRIDbXWS0gVURyAca+Z5qtQe1gWRUQoEVSEm0LCIAiilhFERNCmTQ+oTQTRpmgT7WoVUhm0apMYQaISLVqEPcBIyt6ZmpFZlpp93/UeuV5ver3aH37MmTmvmTMz55xIxj9ieHg4k6yF2ICd2IQyFCCCAfTgOepwF22RSKSPY+pBR/NxBA/Qiz+YKAbJfI9aVCFr0t4oFEEZ7mAIIUz/xnd0oxNf8RMDiL+ZPs6PIyexQ4cjGmSarsYFrIHDOIRXcIjuoxXdGEQelmIdtmIzCmH8QC1OMawdXhgTdLYSLQh36R3XoALj7jJUJi8Tc7ELzxDC+hcxti4XlqAZoaN3pA9hTmg0lSPlV+MW+mF8w0E4ShkZJLJxFuEdWXAfZqfSQWIZ6vlxXYftefMdqIyWI7ECL2D8gh3PSmxkKufUX4VWhJG6RjrPJzPjZSyjkWPJVBpOVpY2/Kp3wN/GeIv1dpaFPbiKjckqp3uN9vyFfDr/w6PptpNSPTuA/6dRP/KVpFQ1rUKPqdUfq1nuEO7mxJ8yO3ZxJg9Fce2W+CK7uFCM0dlkJnuLa2vQCdPO8vG/O3vtk7mEOL9NPlNTKM1wjm1Js+40qvF0TjFr4Uw+Y0F7ubF2F0Ub5aQIN/ERJzGtqSr+TmnrGD7gNkqdQVxCvGC0oyK+Qrpp2lmGNhgutpV25hA6JxrO1HWYl24n1qN+IW4grCQPSZeZ4Re5H2ENci47jdx0OqReDk4gTFOuJK6No2taPieX4Opq9OAcprQCUN73fwZd8KZt7wrCdmHk/rlQAIcwPLqF72EDJlxIzYdfs/WtZ3hswIIwQmNmDTLKyTiP7fAnH0YnGtCIp/gEJ1f3FqVwc1SFaviJ26YbomYcZsPzhGPyoMNiXIZjHcKnddv2Be4P22NHz70eRoNkdOhqOC5O3kPCVQrmYS8cRhsMQ0MyaZjvPrIJB+CueVyMGcbEXCo53s6b27AFy+HLdoid73rxBk2oxyOG7TPHpPEXgxKOAep5ie4AAAAASUVORK5CYII="
      alt="Enphase Logo"
      className={`w-10 h-10 rounded-full object-contain flex-shrink-0 ${className}`}
    />
  );

  EnphaseLogo.propTypes = {
    className: PropTypes.string,
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden fixed inset-0 w-full m-0 p-0">
      <header className="bg-[#ff8800] w-full py-3 px-6 z-10 relative flex-shrink-0">
        <div className="flex items-center gap-4 max-w-full">
          <EnphaseLogo />
          <span className="text-white text-base md:text-sm font-medium whitespace-nowrap">
            EV Charger Fleet Management System
          </span>
        </div>
      </header>

      <div className="flex-1 relative overflow-hidden min-h-0 w-full">
        <div className="w-full h-full relative overflow-hidden">
          <img src={loginSideImg} alt="EV Charging Station" className="w-full h-full object-cover object-center" />
          <div className="absolute left-[5%] top-1/2 -translate-y-1/2 md:left-[4%] z-[1] pointer-events-none">
            <h1 className="text-white text-5xl md:text-4xl sm:text-3xl font-bold mb-2 drop-shadow-[0_2px_4px_rgba(0,0,0,0.3)] leading-tight">
              Efficient EV Fleet Charging <br />& Management System
            </h1>
          </div>
          <div className="absolute right-[5.75em] top-0 bottom-0 flex items-center justify-center p-10 bg-transparent z-20 sm:p-6">
            <div className="w-[480px] min-w-[400px] max-w-[480px] bg-white rounded-lg p-8 shadow-[0_4px_24px_rgba(0,0,0,0.15)] sm:w-[400px] sm:min-w-[350px] sm:max-w-[400px]">
              <h2 className="text-3xl font-bold mb-4 text-gray-800">Sign In</h2>
              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                Use your Enlighten Manager credentials to submit applications on behalf of homeowners.
              </p>
              <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
                <div className="relative flex items-center">
                  <input
                    type="email"
                    placeholder="Email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    autoComplete="username"
                    required
                    className="w-full py-3 px-3 pr-10 border border-gray-300 rounded focus:border-[#ff8800] outline-none transition-colors text-sm placeholder:text-gray-400"
                  />
                </div>
                <div className="relative flex items-center">
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    autoComplete="current-password"
                    required
                    className="w-full py-3 px-3 pr-10 border border-gray-300 rounded focus:border-[#ff8800] outline-none transition-colors text-sm placeholder:text-gray-400"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 text-gray-400 hover:text-gray-600 cursor-pointer focus:outline-none"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <div className="flex justify-between -mt-2 mb-2">
                  <a
                    href={SIGNUP_URL}
                    className="text-blue-600 text-sm no-underline cursor-pointer hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    SignUp
                  </a>
                  <a
                    href={FORGOT_PASSWORD_URL}
                    className="text-blue-600 text-sm no-underline cursor-pointer hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Forgot Password
                  </a>
                </div>
                <button
                  type="submit"
                  className="bg-[#ff8800] text-white border-none rounded py-3.5 px-6 text-sm font-semibold uppercase cursor-pointer transition-colors mt-2 hover:bg-[#e67700] disabled:opacity-60 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  {loading ? "Signing In..." : "SIGN IN"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <footer className="bg-white w-full py-3 px-6 z-10 relative flex-shrink-0 border-t border-gray-200">
        <div className="flex items-center gap-3 whitespace-nowrap">
          <img src={enphaseLogoWithText} alt="Enphase" className="h-6 w-auto object-contain flex-shrink-0" />
          <span className="text-gray-600 text-xs mr-auto">{getCopyrightText()}</span>
          <div className="flex items-center gap-2">
            <a
              href={PRIVACY_URL}
              className="text-blue-600 text-xs no-underline cursor-pointer hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy
            </a>
            <span className="text-gray-400 text-xs">|</span>
            <a
              href={TERMS_URL}
              className="text-blue-600 text-xs no-underline cursor-pointer hover:underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Login;
